import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Pending as PendingIcon,
  RadioButtonUnchecked as UncheckedIcon,
  FolderOpen as FolderOpenIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import {
  selectProductMaterialData,
  selectCollaborativeFormDataForMaterial,
  selectFormData,
  selectBasicInformationFromFormData,
  selectFormStatus,
} from "../selectors/collaborativeForm.selector";
import { getFormById } from "../reducers/collaborativeForm.reducer";
import LeftSideNavbar from "../components/leftSideNavbar.component";
import TopBreadcrumbs from "../components/topBreadcrumbs.component";
import { PageStyles } from "../styles";

const MaterialDetails = () => {
  const { materialId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const productMaterialData = useSelector(selectProductMaterialData);
  const collaborativeFormData = useSelector(
    selectCollaborativeFormDataForMaterial(materialId)
  );

  // Get the form data from the collaborative form state
  const formData = useSelector(selectFormData);
  const formStatus = useSelector(selectFormStatus);
  const basicInformation = useSelector(selectBasicInformationFromFormData);

  // Function to get status chip styling
  const getStatusStyling = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
      case "completed":
        return {
          backgroundColor: "#E6F4EA",
          color: "#137333",
          border: "1px solid rgba(18, 183, 106, 0.30)",
        };
      case "submitted":
      case "submitted_with_note":
        return {
          backgroundColor: "#E6F4EA",
          color: "#137333",
          border: "1px solid rgba(18, 183, 106, 0.30)",
        };
      case "pending":
        return {
          backgroundColor: "#FEF3C7",
          color: "#B45309",
          border: "1px solid rgba(234, 170, 8, 0.30)",
        };
      case "rejected":
        return {
          backgroundColor: "#FEE2E2",
          color: "#B91C1C",
          border: "1px solid rgba(185, 28, 28, 0.30)",
        };
      default:
        return {
          backgroundColor: "#F2F4F7",
          color: "#344054",
          border: "1px solid #D0D5DD",
        };
    }
  };

  // Function to render status chip
  const renderStatusChip = (status) => {
    const styling = getStatusStyling(status);
    let displayStatus;

    switch (status?.toLowerCase()) {
      case "approved":
        displayStatus = "Approved";
        break;
      case "submitted":
        displayStatus = "Submitted";
        break;
      case "submitted_with_note":
        displayStatus = "Submitted with Note";
        break;
      case "completed":
        displayStatus = "Completed";
        break;
      case "created":
        displayStatus = "Not Submitted";
        break;
      default:
        displayStatus = status || "Unknown";
    }

    return (
      <Chip
        label={displayStatus}
        sx={{
          ...styling,
          fontSize: "14px",
          fontWeight: 500,
          borderRadius: "6px",
          padding: "4px 10px",
          textTransform: "capitalize",
        }}
      />
    );
  };

  // Get brand approval status chip styling
  const getBrandApprovalStyling = (status) => {
    if (!status || status === "Pending") {
      return { backgroundColor: "#F2F4F7", color: "#667085", border: "1px solid #D0D5DD" };
    }
    if (status === "Approved") {
      return { backgroundColor: "#E6F4EA", color: "#137333", border: "1px solid rgba(18, 183, 106, 0.30)" };
    }
    if (status === "Not Approved") {
      return { backgroundColor: "#FEE2E2", color: "#B91C1C", border: "1px solid rgba(185, 28, 28, 0.30)" };
    }
    return { backgroundColor: "#FEF3C7", color: "#B45309", border: "1px solid rgba(234, 170, 8, 0.30)" };
  };

  // Render brand approval status chip
  const renderBrandApprovalChip = (status) => {
    const displayStatus = (!status || status === "Pending") ? "Pending" : status;
    return (
      <Chip
        label={displayStatus}
        sx={{ ...getBrandApprovalStyling(status), fontSize: "14px", fontWeight: 500, borderRadius: "6px", padding: "4px 10px" }}
      />
    );
  };

  const [materialData, setMaterialData] = useState(null);

  useEffect(() => {
    // Find the material data based on materialId
    if (productMaterialData && materialId) {
      const foundMaterial = productMaterialData.find(
        (material) => material._id === materialId
      );
      setMaterialData(foundMaterial);
    }
  }, [productMaterialData, materialId]);

  // Fetch form data when component mounts
  useEffect(() => {
    if (materialId) {
      dispatch(getFormById({ formId: materialId }));
    }
  }, [dispatch, materialId]);

  const [activeTab, setActiveTab] = useState("supplyChain");

  // Use form data if available, otherwise use mock data
  const displayData = formData || materialData;


  // Check conditional logic for sections
  // This ensures that sections like "Fabric Trader / Warehouser / Processor" 
  // only show when their conditional criteria are met (e.g., when fabric_trader_warehouser_processor = "Yes")
  const shouldShowSection = (section, allFormData) => {
    if (!section.conditional) return true;

    const { section: conditionalSection, field, condition, value } = section.conditional;
    
    // Find the conditional section data by looking for the section with matching sectionId
    let conditionalSectionData = {};
    
    // Look for the conditional section in the form data
    Object.keys(allFormData).forEach(sectionKey => {
      const sectionData = allFormData[sectionKey];
      if (sectionData && sectionData.sectionId === conditionalSection) {
        conditionalSectionData = sectionData;
      }
    });
    
    // Get the field value from the conditional section
    // First try to get from the data object, then fallback to defaultValue from the field config
    let fieldValue = conditionalSectionData[field];
    
    // If no value in data, try to get from the field configuration
    if (fieldValue === undefined && conditionalSectionData.fields) {
      const fieldConfig = conditionalSectionData.fields.find(f => f.id === field);
      if (fieldConfig && fieldConfig.defaultValue) {
        fieldValue = fieldConfig.defaultValue;
      }
    }
    
    // Also check if the field value is in the data object directly
    if (fieldValue === undefined && conditionalSectionData.data && conditionalSectionData.data[field]) {
      fieldValue = conditionalSectionData.data[field];
    }
    
    switch (condition) {
      case 'equals':
        return fieldValue === value;
      case 'not_equals':
        return fieldValue !== value;
      case 'contains':
        return fieldValue && fieldValue.toLowerCase().includes(value.toLowerCase());
      default:
        return true;
    }
  };

  // Get sections from form data for overview table
  // The form data structure has sections directly, not formStructure.sections
  const sections = formData?.sections
    ? (() => {
        // Get all sections and filter based on conditional logic
        const allSections = Object.values(formData.sections);
        const visibleSections = allSections.filter(section => shouldShowSection(section, formData.sections));

        return visibleSections.map((section) => {
          // Map the section data to include brandActions fields for the table
          const sectionBrandActions = section.brandActions || {};
          const sectionMetadata = section.metadata || {}; // Fallback for legacy data

          return {
            ...section,
            // Map brandActions fields to table columns (prioritize brandActions over legacy metadata)
            reformationNote:
              sectionBrandActions.noteToVendor ||
              sectionMetadata.notetovendor ||
              sectionMetadata.requestNote ||
              "-",
            // Map status fields - use section.status directly like supplier platform
            status: section.status || "",
            brandStatus:
              sectionBrandActions.status ||
              sectionMetadata.approval ||
              sectionMetadata.status ||
              "", // Reformation approval status - check brandActions.status first
          };
        });
      })()
    : [];

  const renderStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return (
          <CheckCircleOutlineIcon sx={{ color: "#039855", fontSize: "24px" }} />
        ); // Green tick - CAP Management style
      case "submitted_with_note":
        return (
          <CheckCircleOutlineIcon sx={{ color: "#f59e0b", fontSize: "24px" }} />
        ); // Yellow warning icon
      default:
        return (
          <CheckCircleOutlineIcon sx={{ color: "#D0D5DD", fontSize: "24px" }} />
        ); // Gray empty - CAP Management style
    }
  };

  // Separate function for reformation approval status
  const renderReformationStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
      case "approve":
        return (
          <CheckCircleOutlineIcon sx={{ color: "#039855", fontSize: "24px" }} />
        ); // Green tick - CAP Management style
      case "reject":
      case "rejected":
        return <CancelIcon sx={{ color: "#ef4444", fontSize: "24px" }} />; // Red cross
      case "pending":
        return (
          <CheckCircleOutlineIcon sx={{ color: "#f59e0b", fontSize: "24px" }} />
        ); // Yellow tick
      default:
        return (
          <CheckCircleOutlineIcon sx={{ color: "#D0D5DD", fontSize: "24px" }} />
        ); // Gray empty - CAP Management style
    }
  };

  const handleBreadcrumbClick = (path) => {
    navigate(path);
  };

  return (
    <Box sx={PageStyles.containerBox}>
      <LeftSideNavbar />
      <Box sx={PageStyles.containerBoxExceptNavbar}>
        <Box sx={PageStyles.firstSectionWithBreadcrumbsAndName}>
          {/* <TopBreadcrumbs /> */}

          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography gutterBottom sx={PageStyles.name}>
                {basicInformation?.data?.fabricName ||
                  displayData?.fabricName ||
                  "Material Details"}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <Divider />

        <Grid container spacing={2} sx={PageStyles.secondSectionGrid}>
          
          {/* Back to To Do Button */}
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/todo")}
              sx={{
                textTransform: "none",
                backgroundColor: "#2D3282",
                borderRadius: "8px",
                padding: "10px 16px",
                fontSize: "14px",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "#1E1F5C",
                },
              }}
            >
              Back to To Do
            </Button>
          </Grid>

          {/* Material Information and Status Cards */}
          <Grid item xs={12} md={8} sx={{ display: "flex" }}>
            <Card
              sx={{
                ...PageStyles.cardForTabsAndBelow,
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "18px",
                  color: "#101828",
                  my: 3,
                }}
              >
                Material Information
              </Typography>

              <Grid container spacing={3} sx={{ flex: 1 }}>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#667085",
                        mb: 1,
                      }}
                    >
                      Fabric Name
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#101828",
                        mb: 2,
                      }}
                    >
                      {basicInformation?.fabricName?.defaultValue ||
                        displayData?.fabricName ||
                        "-"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#667085",
                        mb: 1,
                      }}
                    >
                      Brand Approval Status
                    </Typography>
                    <Box>
                      {renderBrandApprovalChip(materialData?.brandApprovalStatus)}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#667085",
                        mb: 1,
                      }}
                    >
                      Material Type
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#101828",
                      }}
                    >
                      {basicInformation?.materialType?.defaultValue ||
                        displayData?.materialType ||
                        "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#667085",
                        mb: 1,
                      }}
                    >
                      Supplier Article
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#101828",
                      }}
                    >
                      {basicInformation?.vendorArticle?.defaultValue ||
                        displayData?.vendorArticle ||
                        "-"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* Material Status Card */}
          <Grid item xs={12} md={4} sx={{ display: "flex" }}>
            <Card
              sx={{
                ...PageStyles.cardForTabsAndBelow,
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "18px",
                  color: "#101828",
                  my: 3,
                }}
              >
                Material Data Request Status
              </Typography>

              <Box sx={{ mb: 3 }}>{renderStatusChip(formStatus)}</Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#667085",
                    mb: 1,
                  }}
                >
                  Last Update Date
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#101828",
                  }}
                >
                  {moment(displayData.lastUpdateDate).format("MMM DD, YYYY")}
                </Typography>
              </Box>
            </Card>
          </Grid>

          {/* Traceability Process Overview */}
          <Grid item xs={12}>
            <Card sx={PageStyles.cardForTabsAndBelow}>
              {/* Action Buttons */}
              <Tabs
                value={activeTab}
                onChange={(event, newValue) => setActiveTab(newValue)}
                sx={{ my: 3 }}
              >
                <Tab
                  label="Material Supply chain Information"
                  value="supplyChain"
                />
              </Tabs>

              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 3 }}
              >
                <Grid item>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: "18px",
                      color: "#101828",
                    }}
                  >
                    Traceability Process Overview
                  </Typography>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    startIcon={<FolderOpenIcon />}
                    onClick={() => {
                      // Navigate to JsonFormRenderer with the materialId
                      navigate(`/form/${materialId}`);
                    }}
                    sx={{
                      textTransform: "none",
                      backgroundColor: "#2D3282",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      fontSize: "14px",
                      fontWeight: "600",
                      "&:hover": {
                        backgroundColor: "#2D31A6",
                      },
                    }}
                  >
                    Open Detailed Information
                  </Button>
                </Grid>
              </Grid>

              <TableContainer
                component={Paper}
                sx={{ boxShadow: "none", minHeight: "40vh" }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#101828",
                          borderBottom: "1px solid #EAECF0",
                          padding: "16px 0",
                        }}
                      >
                        Section
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#101828",
                          borderBottom: "1px solid #EAECF0",
                          padding: "16px 0",
                        }}
                      >
                        Supplier Completion Status
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#101828",
                          borderBottom: "1px solid #EAECF0",
                          padding: "16px 0",
                        }}
                      >
                        Reformation Approval Status
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#101828",
                          borderBottom: "1px solid #EAECF0",
                          padding: "16px 0",
                        }}
                      >
                        Reformation Note
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sections.map((section, index) => (
                      <TableRow key={index}>
                        <TableCell
                          sx={{
                            fontSize: "14px",
                            color: "#101828",
                            borderBottom: "1px solid #EAECF0",
                            padding: "16px 0",
                          }}
                        >
                          {section.title}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #EAECF0",
                            padding: "16px 0",
                          }}
                        >
                          {renderStatusIcon(section.status)}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #EAECF0",
                            padding: "16px 0",
                          }}
                        >
                          {renderReformationStatusIcon(section?.brandStatus)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "14px",
                            color: "#667085",
                            borderBottom: "1px solid #EAECF0",
                            padding: "16px 0",
                          }}
                        >
                          {section.reformationNote || "-"}
                        </TableCell>
                      </TableRow>
                    )) || (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{ textAlign: "center", py: 4 }}
                        >
                          <Typography sx={{ color: "#667085" }}>
                            {formData
                              ? "No sections data available"
                              : "Loading form data..."}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default MaterialDetails;
